/**
 * reportError — the single funnel for client-side errors.
 *
 * Centralizes error reporting to two sinks, each enriched with the current
 * identity (user, task, trace):
 *   1. PostHog — the queryable home for client exceptions.
 *   2. The server diag endpoint — backup channel (PostHog can be ad-blocked).
 *
 * Identity is resolved lazily from module-scoped accessors so any caller (error
 * boundaries, route-level error.tsx, the global-error fallback) gets the right
 * user/task/trace without having to thread them through props.
 *
 * Hard guarantee: reportError NEVER throws. Diagnostics must not break the page.
 */

import { posthog } from "@/lib/posthog";
import { getCurrentUserId } from "@/lib/clientIdentity";
import { getSelectedTaskId } from "@/stores/socketService";
import { getCurrentTraceId } from "@/lib/clientTrace";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

export interface ReportContext {
  boundary?: string;
  componentStack?: string;
  userId?: string;
  taskId?: string;
  traceId?: string;
}

export function reportError(error: unknown, ctx: ReportContext = {}): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    const boundary = ctx.boundary;
    const componentStack = ctx.componentStack;

    // Resolve identity from context first, then fall back to the live accessors.
    const userId = ctx.userId ?? getCurrentUserId();
    const taskId = ctx.taskId ?? getSelectedTaskId?.() ?? undefined;
    const traceId = ctx.traceId ?? getCurrentTraceId?.() ?? undefined;

    // Sink 1 — PostHog.
    try {
      posthog.captureException(err, {
        boundary,
        trace_id: traceId,
        task_id: taskId,
        user_id: userId,
        componentStack,
      });
    } catch {
      // ignore — diagnostics must never break the page
    }

    // Sink 2 — server diag endpoint. The route spreads `...parsed`, so the
    // identity fields flow through with no server change.
    try {
      fetch(`${SERVER_URL}/api/diag/react-error`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        credentials: "omit",
        body: JSON.stringify({
          kind: "boundary",
          boundary,
          msg: err.message,
          stack: err.stack,
          componentStack,
          url: typeof window !== "undefined" ? window.location.href : null,
          at: new Date().toISOString(),
          userId,
          taskId,
          traceId,
        }),
      }).catch(() => {});
    } catch {
      // ignore — diagnostics must never break the page
    }
  } catch {
    // Absolute backstop — reportError must NEVER throw.
  }
}
